<script lang="ts" setup>
import { computed } from 'vue'

import { useDefaults } from '../../composables'
import { isUiKitColor } from '../../utils/color-utils'

interface BProgressProps {
	/** Variant of the progress indicator */
	variant?: 'circular' | 'linear'
	/**
	 * Size of the spinner or width of the progress bar. For the circular variant, it represents the diameter.
	 * Can be a number (treated as pixels) or any valid CSS size unit.
	 */
	size?: string | number
	/** Color of the progress indicator. */
	color?: string
	/** Thickness of the circular ring stroke or the height of the linear progress bar. */
	thickness?: number
	/** Progress value from 0 to 100.*/
	progress?: number
	/** Whether the progress indicator is indeterminate.*/
	indeterminate?: boolean
	/** Background color for the track in determinate mode (used for both circular & linear).*/
	bgColor?: string
}

const _props = withDefaults(defineProps<BProgressProps>(), {
	variant: 'circular',
	size: '50px',
	color: 'primary',
	thickness: 4,
	progress: 0,
	indeterminate: false,
	bgColor: 'surface-variant-1'
})
const props = useDefaults(_props)

const getSizeWithUnit = (value: string | number): string =>
	typeof value === 'number' ? `${value}px` : value

const normalizedSize = computed(() =>
	typeof props.size === 'number' ? props.size : parseInt(props.size, 10) || 50
)
const center = computed(() => normalizedSize.value / 2)
const radius = computed(() => center.value - props.thickness / 2)
const circumference = computed(() => 2 * Math.PI * radius.value)
const clampedProgress = computed(() =>
	Math.min(Math.max(props.progress || 0, 0), 100)
)
const strokeDashoffset = computed(
	() => circumference.value * (1 - clampedProgress.value / 100)
)

const linearWidth = computed(() => getSizeWithUnit(props.size))
const linearHeight = computed(() => getSizeWithUnit(props.thickness))
const linearProgressWidth = computed(() => `${clampedProgress.value}%`)

const computedColor = computed(() => {
	if (isUiKitColor(props.color)) {
		return getComputedStyle(document.documentElement)
			.getPropertyValue(`--color-${props.color}`)
			.trim()
	}
	return props.color
})

const computedBgColor = computed(() => {
	if (isUiKitColor(props.bgColor)) {
		return getComputedStyle(document.documentElement)
			.getPropertyValue(`--color-${props.bgColor}`)
			.trim()
	}
	return props.bgColor
})
</script>

<template>
	<div
		:aria-valuemin="props.indeterminate ? undefined : 0"
		:aria-valuemax="props.indeterminate ? undefined : 100"
		:aria-valuenow="props.indeterminate ? undefined : clampedProgress"
		:aria-busy="props.indeterminate ? 'true' : undefined"
		:aria-label="
			props.indeterminate ? 'Loading' : `Progress: ${clampedProgress}%`
		"
		:style="{
			width:
				props.variant === 'circular' ? getSizeWithUnit(props.size) : undefined,
			height:
				props.variant === 'circular' ? getSizeWithUnit(props.size) : undefined
		}"
		role="progressbar"
	>
		<div
			v-if="props.variant === 'circular'"
			:style="{
				width: getSizeWithUnit(props.size),
				height: getSizeWithUnit(props.size)
			}"
			class="inline-block relative align-middle leading-none"
		>
			<svg
				v-if="!props.indeterminate"
				:width="getSizeWithUnit(props.size)"
				:height="getSizeWithUnit(props.size)"
				:viewBox="`0 0 ${normalizedSize} ${normalizedSize}`"
				xmlns="http://www.w3.org/2000/svg"
			>
				<circle
					:cx="center"
					:cy="center"
					:r="radius"
					:stroke="computedBgColor"
					:stroke-width="props.thickness"
					fill="none"
				/>

				<circle
					:cx="center"
					:cy="center"
					:r="radius"
					:stroke="computedColor"
					:stroke-width="props.thickness"
					:stroke-dasharray="circumference"
					:stroke-dashoffset="strokeDashoffset"
					:transform="`rotate(-90 ${center} ${center})`"
					fill="none"
					stroke-linecap="round"
				/>
			</svg>

			<svg
				v-else
				:width="getSizeWithUnit(props.size)"
				:height="getSizeWithUnit(props.size)"
				:viewBox="`0 0 ${normalizedSize} ${normalizedSize}`"
				class="animate-spin"
				xmlns="http://www.w3.org/2000/svg"
			>
				<circle
					:cx="center"
					:cy="center"
					:r="radius"
					:stroke="computedColor"
					:stroke-width="props.thickness"
					:stroke-dasharray="`${circumference / 2} ${(circumference * 3) / 4}`"
					:transform="`rotate(-90 ${center} ${center})`"
					fill="none"
					stroke-linecap="round"
				/>
			</svg>
		</div>

		<div
			v-else-if="props.variant === 'linear'"
			:style="{
				width: linearWidth,
				height: linearHeight,
				backgroundColor: computedBgColor
			}"
			class="relative overflow-hidden rounded-full"
		>
			<div
				v-if="!props.indeterminate"
				:style="{ width: linearProgressWidth, backgroundColor: computedColor }"
				class="h-full rounded-full transition-all duration-300 ease-linear"
			/>

			<div
				v-else
				:style="{ backgroundColor: computedColor }"
				class="b-progress-linear__indicator indeterminate absolute top-0 right-0 bottom-0 left-auto w-1/3"
			/>
		</div>
	</div>
</template>

<style scoped>
@keyframes linearIndeterminateLTR {
	0% {
		transform: translateX(-150%);
	}
	100% {
		transform: translateX(350%);
	}
}

@keyframes linearIndeterminateRTL {
	0% {
		transform: translateX(150%);
	}
	100% {
		transform: translateX(-350%);
	}
}

.b-progress-linear__indicator.indeterminate {
	animation: linearIndeterminateRTL 1.5s infinite;
}

[dir='ltr'] .b-progress-linear__indicator.indeterminate {
	animation: linearIndeterminateLTR 1.5s infinite;
	left: 0;
}
</style>
